import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Icon, LoadingSpinner } from '~/components/granular';
import { classNames } from '~/utils/styles';
import type { ButtonProps } from './Button.types';

const buttonVariants = cva(
  'flex cursor-pointer items-center justify-between whitespace-nowrap disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500',
  {
    compoundVariants: [
      {
        appearance: ['default', 'secondary', 'ghost', 'danger', 'text'],
        class: 'button-200',
        size: ['x-small', 'small'],
      },
      {
        appearance: ['default', 'secondary', 'ghost', 'danger', 'text'],
        class: 'button-100',
        size: ['medium', 'large'],
      },
      {
        appearance: ['filter', 'input'],
        class: 'body-200-light',
        size: ['small', 'medium', 'large'],
      },
      {
        appearance: ['filter', 'input'],
        class: ['body-250-light', 'px-1.5'],
        size: 'x-small',
      },
    ],
    defaultVariants: {
      appearance: 'default',
      size: 'medium',
    },
    variants: {
      appearance: {
        danger: 'bg-red-700 p-3 text-white hover:bg-red-800 focus:bg-red-800 active:bg-red-900',
        default: 'bg-teal-700 p-3 text-white hover:bg-teal-800 focus:bg-teal-900 active:bg-teal-900',
        filter:
          'cursor-pointer border border-neutral-400 px-2 py-2 leading-4 hover:border-teal-700 hover:text-teal-700 disabled:border-solid disabled:border-neutral-300 disabled:hover:border-neutral-300',
        ghost: 'bg-transparent p-3 hover:bg-neutral-200 focus:bg-neutral-300 active:bg-neutral-300',
        input:
          'w-full cursor-pointer border border-neutral-400 px-2 py-2 leading-4 hover:border-teal-700 hover:text-teal-700 disabled:border-solid disabled:border-neutral-300 disabled:hover:border-neutral-300',
        secondary:
          'border border-solid border-neutral-300 bg-white p-3 hover:border-teal-500 hover:bg-teal-100 hover:text-teal-700 focus:bg-teal-200 focus:text-teal-700 active:border-teal-500 active:bg-teal-200 active:text-teal-700 disabled:border-none',
        text: 'px-0 py-0 text-black hover:text-teal-700 disabled:bg-transparent disabled:hover:bg-transparent',
      },
      size: {
        large: 'h-10',
        medium: 'h-9',
        small: 'h-8',
        'x-small': 'h-7',
      },
    },
  },
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      appearance = 'default',
      className,
      children,
      leftIcon,
      rightIcon,
      loading,
      disabled = false,
      size = 'large',
      type = 'button',
      ...props
    },
    forwardedRef,
  ) => {
    const classes = classNames(buttonVariants({ appearance, size }), className);

    return (
      <button className={classes} disabled={disabled || loading} ref={forwardedRef} type={type} {...props}>
        <ButtonContent leftIcon={leftIcon} loading={loading} rightIcon={rightIcon}>
          {children}
        </ButtonContent>
      </button>
    );
  },
);

const iconSize = { height: 16, width: 16 };

function ButtonContent({ children, leftIcon, rightIcon, loading }: ButtonProps) {
  return (
    <>
      {loading && <LoadingSpinner {...iconSize} className="text-neutral-900" />}
      {!loading && leftIcon && <Icon icon={leftIcon} {...iconSize} className="mr-1" />}
      {children}
      {rightIcon && <Icon icon={rightIcon} {...iconSize} className="ml-1" />}
    </>
  );
}
